import React, { useState, useRef } from 'react';
import {
  Container, Box, Typography, Paper, Grid, Card, CardMedia,
  CardContent, CardActions, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Chip, Tabs, Tab,
  TextField, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon, Add as AddIcon,
  AttachFile as AttachIcon, Save as SaveIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { coverOf, techPhotoOf } from '../utils/helpers';

function TechnicalSheetsSection({
  products, clients, selectedClient,
  handleEditClient, addClientDocument,
  setOpenClientDialog, setProducts, setSnackbar,
  resetClientForm, setActiveSection,
  editingClientId, clientTab, setClientTab,
  clientForm, setClientForm, removeClientDocument,
  handleSaveClient, handleViewClient, handleDeleteClient, openClientDialog
}) {
  const [openTSDialog, setOpenTSDialog] = useState(false);
  const [tsEdit, setTsEdit] = useState(false);
  const [tsProduct, setTsProduct] = useState(null);
  const tsRef = useRef(null);
  const handleOpenTS = (p) => { setTsProduct(p); setTsEdit(false); setOpenTSDialog(true); };
  const handleExportPDF = async () => {
    if (!tsRef.current) {
      if (!openTSDialog && tsProduct) { setOpenTSDialog(true); setTimeout(handleExportPDF, 500); }
      return;
    }
    const node = tsRef.current;
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 24;
    let imgWidth = pageWidth - margin * 2;
    let imgHeight = canvas.height * imgWidth / canvas.width;
    if (imgHeight > pageHeight - margin * 2) {
      imgHeight = pageHeight - margin * 2;
      imgWidth = canvas.width * imgHeight / canvas.height;
    }
    const x = (pageWidth - imgWidth) / 2;
    const y = (pageHeight - imgHeight) / 2;
    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
    pdf.save(`Ficha_${(tsProduct?.codigo || 'producto')}.pdf`);
  };
  
    return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>Fichas Técnicas</Typography>
      </Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          {products.map((p) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`ts-${p.uid}`}>
              <Card>
                <CardMedia component="img" height="160" image={techPhotoOf(p) || coverOf(p)} alt={p.nombre} sx={{ objectFit: 'cover' }} />
                <CardContent>
                  <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>{p.nombre}</Typography>
                  <Typography variant="body2" color="text.secondary">Código: {p.codigo}</Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                  <Button fullWidth variant="outlined" onClick={() => handleOpenTS(p)}>Ver / Editar</Button>
                  <Button fullWidth variant="contained" startIcon={<PdfIcon />} onClick={() => { setTsProduct(p); setTimeout(handleExportPDF, 0); }}>PDF</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <Dialog open={openTSDialog} onClose={() => setOpenTSDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Ficha Técnica — {tsProduct?.nombre}</Typography>
            <IconButton onClick={() => setOpenTSDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {tsProduct && (
            <Box ref={tsRef} sx={{ background: '#fff', color: '#0f172a', p: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              {/* Header de la ficha técnica */}
              <Box sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid #e2e8f0' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>FICHA TÉCNICA</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#6366f1' }}>{tsProduct.nombre}</Typography>
                <Typography variant="body2" color="text.secondary">Código: {tsProduct.codigo}</Typography>
              </Box>
              
              <Grid container spacing={3}>
                {/* Imagen del producto */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ textAlign: 'center' }}>
                    <img src={techPhotoOf(tsProduct) || coverOf(tsProduct)} alt={tsProduct.nombre} 
                         style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  </Box>
                </Grid>
                
                {/* Información técnica detallada */}
                <Grid item xs={12} md={7}>
                  {/* Especificaciones básicas */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#374151', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                      Especificaciones Básicas
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Marca" value={tsProduct.techSheet?.marca || tsProduct.marca || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), marca: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Marca:</strong> {tsProduct.techSheet?.marca || tsProduct.marca || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Categoría" value={tsProduct.categoria || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, categoria: e.target.value}))} />
                        ) : (
                          <Typography variant="body2"><strong>Categoría:</strong> {tsProduct.categoria || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Material" value={tsProduct.techSheet?.material || tsProduct.material || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), material: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Material:</strong> {tsProduct.techSheet?.material || tsProduct.material || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Medidas" value={tsProduct.medidas || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, medidas: e.target.value}))} />
                        ) : (
                          <Typography variant="body2"><strong>Medidas:</strong> {tsProduct.medidas || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Peso (kg)" value={tsProduct.peso_equipo || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, peso_equipo: e.target.value}))} />
                        ) : (
                          <Typography variant="body2"><strong>Peso:</strong> {tsProduct.peso_equipo || '—'} kg</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* Información eléctrica */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#374151', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                      Especificaciones Eléctricas
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Voltaje" value={tsProduct.voltaje || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, voltaje: e.target.value}))} />
                        ) : (
                          <Typography variant="body2"><strong>Voltaje:</strong> {tsProduct.voltaje || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={4}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Amperaje (A)" value={tsProduct.amperaje || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, amperaje: e.target.value}))} />
                        ) : (
                          <Typography variant="body2"><strong>Amperaje:</strong> {tsProduct.amperaje || '—'} A</Typography>
                        )}
                      </Grid>
                      <Grid item xs={4}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Potencia (W)" value={tsProduct.potencia || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, potencia: e.target.value}))} />
                        ) : (
                          <Typography variant="body2"><strong>Potencia:</strong> {tsProduct.potencia || '—'} W</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                
                {/* Información operativa - ancho completo */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#374151', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                      Información Operativa
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="N° Operarios" type="number" value={tsProduct.operarios ?? ''}
                            onChange={e=>setTsProduct(tp=>({...tp, operarios: Number(e.target.value)||0}))} />
                        ) : (
                          <Typography variant="body2"><strong>N° Operarios:</strong> {tsProduct.operarios ?? '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Volumen (m³)" type="number" step="0.1" value={tsProduct.volumen_m3 ?? ''}
                            onChange={e=>setTsProduct(tp=>({...tp, volumen_m3: Number(e.target.value)||0}))} />
                        ) : (
                          <Typography variant="body2"><strong>Volumen:</strong> {tsProduct.volumen_m3 ?? '—'} m³</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Capacidad niños" value={tsProduct.techSheet?.capacidad_ninos || tsProduct.capacidad_ninos || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), capacidad_ninos: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Cap. niños:</strong> {tsProduct.techSheet?.capacidad_ninos || tsProduct.capacidad_ninos || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Capacidad adultos" value={tsProduct.techSheet?.capacidad_adultos || tsProduct.capacidad_adultos || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), capacidad_adultos: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Cap. adultos:</strong> {tsProduct.techSheet?.capacidad_adultos || tsProduct.capacidad_adultos || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Edades permitidas" value={tsProduct.edades_permitidas || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, edades_permitidas: e.target.value}))} />
                        ) : (
                          <Typography variant="body2"><strong>Edades:</strong> {tsProduct.edades_permitidas || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Tiempo armado" value={tsProduct.techSheet?.tiempo_armado || tsProduct.tiempo_armado || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), tiempo_armado: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>T. armado:</strong> {tsProduct.techSheet?.tiempo_armado || tsProduct.tiempo_armado || '—'}</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* Requisitos de instalación */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#374151', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                      Requisitos de Instalación
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Área requerida" value={tsProduct.techSheet?.area_requerida || tsProduct.area_requerida || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), area_requerida: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Área requerida:</strong> {tsProduct.techSheet?.area_requerida || tsProduct.area_requerida || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Tipo de terreno" value={tsProduct.techSheet?.terreno || tsProduct.terreno || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), terreno: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Terreno:</strong> {tsProduct.techSheet?.terreno || tsProduct.terreno || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Viento máximo" value={tsProduct.techSheet?.viento_max || tsProduct.viento_max || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), viento_max: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Viento máx:</strong> {tsProduct.techSheet?.viento_max || tsProduct.viento_max || '—'}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {tsEdit ? (
                          <TextField fullWidth size="small" label="Anclajes" value={tsProduct.techSheet?.anclajes || tsProduct.anclajes || ''}
                            onChange={e=>setTsProduct(tp=>({...tp, techSheet:{...(tp.techSheet||{}), anclajes: e.target.value}}))} />
                        ) : (
                          <Typography variant="body2"><strong>Anclajes:</strong> {tsProduct.techSheet?.anclajes || tsProduct.anclajes || '—'}</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* Observaciones */}
                  {(tsProduct.observaciones || tsEdit) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#374151', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                        Observaciones
                      </Typography>
                      {tsEdit ? (
                        <TextField fullWidth multiline rows={3} size="small" label="Observaciones" value={tsProduct.observaciones || ''}
                          onChange={e=>setTsProduct(tp=>({...tp, observaciones: e.target.value}))} />
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{tsProduct.observaciones || '—'}</Typography>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
              
              {/* Footer de la ficha */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '2px solid #e2e8f0', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  INFLABLES DA SILVA - Ficha Técnica - {new Date().toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTSDialog(false)}>Cerrar</Button>
           <Button
            variant="outlined"
            startIcon={tsEdit ? <SaveIcon /> : <EditIcon />}
            onClick={async () => {
              if (tsEdit) {
                // 1) Guardar cambios en el producto principal (local)
                setProducts(prev => prev.map(p =>
                  p.uid === tsProduct.uid ? { ...tsProduct } : p
                ));
                // 2) Guardar en el catálogo unificado (backend) para que persista y se refleje en José
                const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
                const token = localStorage.getItem('token');
                const headers = { 'Content-Type': 'application/json' };
                if (token) headers['Authorization'] = 'Bearer ' + token;
                if (tsProduct?.codigo) {
                  try {
                    const res = await fetch(apiBase + '/products/catalog/' + encodeURIComponent(tsProduct.codigo), {
                      method: 'PUT',
                      headers,
                      body: JSON.stringify({
                        nombre: tsProduct.nombre,
                        categoria: tsProduct.categoria,
                        precio_alquiler_5horas: Number(tsProduct.precio_alquiler_5horas) || 0,
                        medidas: tsProduct.medidas,
                        volumen_m3: Number(tsProduct.volumen_m3) || 0,
                        edades_permitidas: tsProduct.edades_permitidas,
                        operarios: Number(tsProduct.operarios) || 1,
                        voltaje: tsProduct.voltaje,
                        amperaje: tsProduct.amperaje,
                        potencia: tsProduct.potencia,
                        peso_equipo: tsProduct.peso_equipo,
                        capacidad_ninos: tsProduct.capacidad_ninos,
                        capacidad_adultos: tsProduct.capacidad_adultos,
                        tiempo_armado: tsProduct.techSheet?.tiempo_armado || tsProduct.tiempo_armado,
                        area_requerida: tsProduct.techSheet?.area_requerida || tsProduct.area_requerida,
                        observaciones: tsProduct.techSheet?.observaciones || tsProduct.observaciones,
                        ficha: {
                          marca: tsProduct.techSheet?.marca,
                          material: tsProduct.techSheet?.material,
                          fabricante: tsProduct.techSheet?.fabricante,
                          pais_origen: tsProduct.techSheet?.pais_origen,
                          fecha_fabricacion: tsProduct.techSheet?.fecha_fabricacion,
                          vida_util: tsProduct.techSheet?.vida_util,
                          norma_tecnica: tsProduct.techSheet?.norma_tecnica,
                          tipo_funcionamiento: tsProduct.techSheet?.tipo_funcionamiento,
                          metodo_operacion: tsProduct.techSheet?.metodo_operacion,
                          tipo_inflador: tsProduct.techSheet?.tipo_inflador || tsProduct.techSheet?.blower,
                          tipo_anclaje: tsProduct.techSheet?.anclajes || tsProduct.techSheet?.tipo_anclaje,
                          peso_max_usuario: tsProduct.techSheet?.peso_max_usuario,
                          altura_max_usuario: tsProduct.techSheet?.altura_max_usuario,
                          viento_max: tsProduct.techSheet?.viento_max,
                          temp_operacion: tsProduct.techSheet?.temp_operacion,
                          restricciones: tsProduct.techSheet?.restricciones,
                          terreno: tsProduct.techSheet?.terreno,
                          no_uso_lluvia: tsProduct.techSheet?.no_uso_lluvia,
                          requiere_electrico: tsProduct.techSheet?.requiere_electrico,
                        },
                      }),
                    });
                    if (!res.ok) {
                      const errData = await res.json().catch(() => ({}));
                      throw new Error(errData.message || errData.error || `HTTP ${res.status}`);
                    }
                    setSnackbar({ open: true, message: 'Ficha técnica guardada en catálogo (José también la verá)', severity: 'success' });
                  } catch (e) {
                    setSnackbar({ open: true, message: 'Guardado local OK, pero falló al guardar en catálogo: ' + e.message, severity: 'warning' });
                  }
                } else {
                  setSnackbar({ open: true, message: 'Ficha técnica actualizada (sin código: no se guardó en catálogo)', severity: 'warning' });
                }
                setTsEdit(false);
              } else {
                setTsEdit(true);
              }
            }}
          >
            {tsEdit ? 'Guardar Cambios' : 'Editar Ficha'}
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={handleExportPDF}>
            Exportar PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

  /** ===== Clientes ===== */
  const renderClients = () => (
    <Container maxWidth="xl">
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Clientes ({clients.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetClientForm(); setActiveSection('clients'); }}>
          Nuevo Cliente
        </Button>
      </Box>
      <Grid container spacing={3}>
        {/* Formulario de Cliente */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              {editingClientId ? 'Editar Cliente' : 'Nuevo Cliente'}
            </Typography>
            
            <Tabs value={clientTab} onChange={(_, v) => setClientTab(v)} sx={{ mb: 2 }}>
              <Tab label="General" />
              <Tab label="Facturación" />
              <Tab label="Documentos" />
            </Tabs>
            
            {/* Pestaña General */}
            {clientTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Cliente *</InputLabel>
                    <Select 
                      value={clientForm.type} 
                      label="Tipo de Cliente *"
                      onChange={e => setClientForm({...clientForm, type: e.target.value})}
                    >
                      <MenuItem value="personal">Personal</MenuItem>
                      <MenuItem value="company">Empresa</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label={clientForm.type === 'company' ? 'Razón Social *' : 'Nombre Completo *'} 
                    value={clientForm.name} 
                    onChange={e=>setClientForm({...clientForm, name:e.target.value})} 
                  />
                </Grid>
                
                {clientForm.type === 'company' && (
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Persona de Contacto *" 
                      value={clientForm.contactPerson || ''} 
                      onChange={e=>setClientForm({...clientForm, contactPerson:e.target.value})} 
                    />
                  </Grid>
                )}
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label={clientForm.type === 'company' ? 'NIT' : 'Cédula'} 
                    value={clientForm.document} 
                    onChange={e=>setClientForm({...clientForm, document:e.target.value})} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Email *" 
                    type="email"
                    value={clientForm.email} 
                    onChange={e=>setClientForm({...clientForm, email:e.target.value})} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Teléfono" 
                    value={clientForm.phone} 
                    onChange={e=>setClientForm({...clientForm, phone:e.target.value})} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Ciudad" 
                    value={clientForm.city} 
                    onChange={e=>setClientForm({...clientForm, city:e.target.value})} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={8}>
                  <TextField 
                    fullWidth 
                    label="Dirección" 
                    value={clientForm.address} 
                    onChange={e=>setClientForm({...clientForm, address:e.target.value})} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField 
                    fullWidth 
                    label="Código Postal" 
                    value={clientForm.postalCode} 
                    onChange={e=>setClientForm({...clientForm, postalCode:e.target.value})} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>¿Cómo nos conoció?</InputLabel>
                    <Select 
                      value={clientForm.howFoundUs} 
                      label="¿Cómo nos conoció?"
                      onChange={e => {
                        setClientForm({
                          ...clientForm, 
                          howFoundUs: e.target.value,
                          referredBy: e.target.value === 'referral' ? clientForm.referredBy : '',
                          otherSource: e.target.value === 'other' ? clientForm.otherSource : ''
                        });
                      }}
                    >
                      <MenuItem value="">-- Seleccione --</MenuItem>
                      <MenuItem value="referral">Referido</MenuItem>
                      <MenuItem value="event">Evento</MenuItem>
                      <MenuItem value="google">Google</MenuItem>
                      <MenuItem value="facebook">Facebook</MenuItem>
                      <MenuItem value="tiktok">TikTok</MenuItem>
                      <MenuItem value="instagram">Instagram</MenuItem>
                      <MenuItem value="youtube">YouTube</MenuItem>
                      <MenuItem value="other">Otros</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>¿Tiene Crédito?</InputLabel>
                    <Select 
                      value={clientForm.hasCredit} 
                      label="¿Tiene Crédito?"
                      onChange={e => setClientForm({...clientForm, hasCredit: e.target.value})}
                    >
                      <MenuItem value={false}>No</MenuItem>
                      <MenuItem value={true}>Sí</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {clientForm.howFoundUs === 'referral' && (
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="¿Quién lo refirió?" 
                      value={clientForm.referredBy} 
                      onChange={e=>setClientForm({...clientForm, referredBy:e.target.value})} 
                    />
                  </Grid>
                )}
                
                {clientForm.howFoundUs === 'other' && (
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Especifique cómo nos conoció" 
                      value={clientForm.otherSource} 
                      onChange={e=>setClientForm({...clientForm, otherSource:e.target.value})} 
                    />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Notas" 
                    multiline 
                    minRows={2} 
                    value={clientForm.notes} 
                    onChange={e=>setClientForm({...clientForm, notes:e.target.value})} 
                  />
                </Grid>
              </Grid>
            )}
            
            {/* Pestaña Facturación */}
            {clientTab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Razón Social" 
                    value={clientForm.billing.companyName} 
                    onChange={e=>setClientForm({
                      ...clientForm, 
                      billing: {...clientForm.billing, companyName: e.target.value}
                    })} 
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Nombre Comercial" 
                    value={clientForm.billing.tradeName} 
                    onChange={e=>setClientForm({
                      ...clientForm, 
                      billing: {...clientForm.billing, tradeName: e.target.value}
                    })} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="NIT/Cédula Facturación" 
                    value={clientForm.billing.taxId} 
                    onChange={e=>setClientForm({
                      ...clientForm, 
                      billing: {...clientForm.billing, taxId: e.target.value}
                    })} 
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Dirección de Facturación" 
                    value={clientForm.billing.address} 
                    onChange={e=>setClientForm({
                      ...clientForm, 
                      billing: {...clientForm.billing, address: e.target.value}
                    })} 
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Persona Encargada de Facturación" 
                    value={clientForm.billing.billingContact} 
                    onChange={e=>setClientForm({
                      ...clientForm, 
                      billing: {...clientForm.billing, billingContact: e.target.value}
                    })} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Teléfono Facturación" 
                    value={clientForm.billing.billingPhone} 
                    onChange={e=>setClientForm({
                      ...clientForm, 
                      billing: {...clientForm.billing, billingPhone: e.target.value}
                    })} 
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Email de Facturación" 
                    type="email"
                    value={clientForm.billing.billingEmail} 
                    onChange={e=>setClientForm({
                      ...clientForm, 
                      billing: {...clientForm.billing, billingEmail: e.target.value}
                    })} 
                  />
                </Grid>
              </Grid>
            )}
            
            {/* Pestaña Documentos */}
            {clientTab === 2 && (
              <Box>
                <Button 
                  variant="outlined" 
                  startIcon={<AttachIcon />} 
                  component="label" 
                  sx={{ mb: 2 }}
                >
                  Adjuntar Documentos
                  <input 
                    hidden 
                    accept="*/*" 
                    multiple 
                    type="file" 
                    onChange={(e) => addClientDocument(e, editingClientId)}
                  />
                </Button>
                
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {(clientForm.documents || []).map((doc, i) => (
                    <Paper 
                      key={i} 
                      variant="outlined" 
                      sx={{ p: 1.5, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{doc.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {doc.category} • {new Date(doc.uploadDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button 
                        size="small" 
                        color="error" 
                        onClick={() => removeClientDocument(i, editingClientId)}
                      >
                        Eliminar
                      </Button>
                    </Paper>
                  ))}
                  {(!clientForm.documents || clientForm.documents.length === 0) && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No hay documentos adjuntos
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            
            {/* Botones de acción */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={handleSaveClient}
                  disabled={!clientForm.name || !clientForm.email}
                >
                  {editingClientId ? 'Actualizar' : 'Guardar'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" onClick={resetClientForm}>
                  Limpiar
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Listado de Clientes */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p:2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Listado de Clientes
            </Typography>
            
            <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
              {clients.map((c) => (
                <Paper 
                  key={c.id}
                  variant="outlined" 
                  sx={{ p: 2, mb: 2, '&:hover': { backgroundColor: '#f8fafc' } }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {c.name}
                        </Typography>
                        <Chip 
                          label={c.type === 'company' ? 'Empresa' : 'Personal'} 
                          size="small" 
                          color={c.type === 'company' ? 'primary' : 'secondary'}
                        />
                        <Chip 
                          label={c.id} 
                          size="small" 
                          variant="outlined"
                        />
                        {c.hasCredit && (
                          <Chip 
                            label="Con Crédito" 
                            size="small" 
                            color="success"
                          />
                        )}
                      </Box>
                      
                      {c.type === 'company' && c.contactPerson && (
                        <Typography variant="body2" color="text.secondary">
                          Contacto: {c.contactPerson}
                        </Typography>
                      )}
                      
                      <Typography variant="body2" color="text.secondary">
                        {c.document} • {c.email} • {c.phone}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        {c.city} — {c.address} {c.postalCode && `(${c.postalCode})`}
                      </Typography>
                      
                      {c.howFoundUs && (
                        <Typography variant="body2" color="text.secondary">
                          Nos conoció: {c.howFoundUs === 'referral' ? `Referido por ${c.referredBy}` : 
                                       c.howFoundUs === 'other' ? c.otherSource : 
                                       c.howFoundUs.charAt(0).toUpperCase() + c.howFoundUs.slice(1)}
                        </Typography>
                      )}
                      
                      {c.documents && c.documents.length > 0 && (
                        <Typography variant="body2" color="primary.main">
                          📎 {c.documents.length} documento(s)
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleViewClient(c)}
                      >
                        Ver Detalle
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleEditClient(c)}
                      >
                        Editar
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="error" 
                        onClick={() => handleDeleteClient(c.id)}
                      >
                        Eliminar
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
              
              {clients.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No hay clientes registrados
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Dialog para ver detalles del cliente */}
      <Dialog open={openClientDialog} onClose={() => setOpenClientDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">
                {selectedClient?.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip 
                  label={selectedClient?.type === 'company' ? 'Empresa' : 'Personal'} 
                  size="small" 
                  color={selectedClient?.type === 'company' ? 'primary' : 'secondary'}
                />
                <Chip label={selectedClient?.id} size="small" variant="outlined" />
                {selectedClient?.hasCredit && (
                  <Chip label="Con Crédito" size="small" color="success" />
                )}
              </Box>
            </Box>
            <IconButton onClick={() => setOpenClientDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedClient && (
            <Grid container spacing={3}>
              {/* Información General */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Información General
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Nombre/Razón Social</Typography>
                  <Typography variant="body1">{selectedClient.name}</Typography>
                </Box>
                
                {selectedClient.type === 'company' && selectedClient.contactPerson && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Persona de Contacto</Typography>
                    <Typography variant="body1">{selectedClient.contactPerson}</Typography>
                  </Box>
                )}
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedClient.type === 'company' ? 'NIT' : 'Cédula'}
                  </Typography>
                  <Typography variant="body1">{selectedClient.document}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedClient.email}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Teléfono</Typography>
                  <Typography variant="body1">{selectedClient.phone}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Dirección</Typography>
                  <Typography variant="body1">
                    {selectedClient.address}
                    {selectedClient.postalCode && ` (${selectedClient.postalCode})`}
                  </Typography>
                </Box>
                
                {selectedClient.howFoundUs && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Cómo nos conoció</Typography>
                    <Typography variant="body1">
                      {selectedClient.howFoundUs === 'referral' ? `Referido por ${selectedClient.referredBy}` : 
                       selectedClient.howFoundUs === 'other' ? selectedClient.otherSource : 
                       selectedClient.howFoundUs.charAt(0).toUpperCase() + selectedClient.howFoundUs.slice(1)}
                    </Typography>
                  </Box>
                )}
                
                {selectedClient.notes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Notas</Typography>
                    <Typography variant="body1">{selectedClient.notes}</Typography>
                  </Box>
                )}
              </Grid>
              
              {/* Información de Facturación */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Información de Facturación
                </Typography>
                
                {selectedClient.billing?.companyName && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Razón Social</Typography>
                    <Typography variant="body1">{selectedClient.billing.companyName}</Typography>
                  </Box>
                )}
                
                {selectedClient.billing?.tradeName && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Nombre Comercial</Typography>
                    <Typography variant="body1">{selectedClient.billing.tradeName}</Typography>
                  </Box>
                )}
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">NIT/Cédula Facturación</Typography>
                  <Typography variant="body1">{selectedClient.billing?.taxId || selectedClient.document}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Dirección Facturación</Typography>
                  <Typography variant="body1">{selectedClient.billing?.address || selectedClient.address}</Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Contacto Facturación</Typography>
                  <Typography variant="body1">
                    {selectedClient.billing?.billingContact || selectedClient.name}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Teléfono Facturación</Typography>
                  <Typography variant="body1">
                    {selectedClient.billing?.billingPhone || selectedClient.phone}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Email Facturación</Typography>
                  <Typography variant="body1">
                    {selectedClient.billing?.billingEmail || selectedClient.email}
                  </Typography>
                </Box>
              </Grid>
              
              {/* Documentos */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Documentos Adjuntos ({selectedClient.documents?.length || 0})
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<AttachIcon />} 
                    component="label"
                    size="small"
                  >
                    Agregar Documento
                    <input 
                      hidden 
                      accept="*/*" 
                      multiple 
                      type="file" 
                      onChange={(e) => addClientDocument(e, selectedClient.id)}
                    />
                  </Button>
                </Box>
                
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {(selectedClient.documents || []).map((doc, i) => (
                    <Paper 
                      key={i} 
                      variant="outlined" 
                      sx={{ p: 1.5, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{doc.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {doc.category} • {new Date(doc.uploadDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button 
                        size="small" 
                        color="error" 
                        onClick={() => removeClientDocument(i, selectedClient.id)}
                      >
                        Eliminar
                      </Button>
                    </Paper>
                  ))}
                  
                  {(!selectedClient.documents || selectedClient.documents.length === 0) && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No hay documentos adjuntos
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenClientDialog(false)}>Cerrar</Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />} 
            onClick={() => {
              setOpenClientDialog(false);
              handleEditClient(selectedClient);
            }}
          >
            Editar Cliente
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );

}

export default TechnicalSheetsSection;
